import React from 'react';
import { Link, Text } from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { findSetting, SettingId } from '../../settings/registry';
import { useSettingFocusId } from '../../hooks/useSettingReveal';

const GENERIC_REASON = 'This setting is not available in the current configuration.';

const matchesFocus = (
    focusId: string | undefined,
    id: SettingId | undefined,
    ids: readonly SettingId[] | undefined,
    idPrefix: string | readonly string[] | undefined
): boolean => {
    if (!focusId) return false;
    if (id && focusId === id) return true;
    if (ids && (ids as readonly string[]).includes(focusId)) return true;
    if (idPrefix) {
        const prefixes = typeof idPrefix === 'string' ? [idPrefix] : idPrefix;
        // Boundary-safe: a prefix names a dotted namespace, not a literal string match, so
        // 'storefront.browseAll.heading' matches itself and '...heading.fontSize' but not some
        // unrelated '...headingSomethingElse' that merely shares the same characters.
        if (prefixes.some((p) => focusId === p || focusId.startsWith(`${p}.`))) return true;
    }
    return false;
};

export interface IConditionalSettingProps {
    /** The real render condition, lifted from the surrounding `{cond && ...}`. Same behavior when true. */
    when: boolean;
    /** A single registered setting id this block governs. */
    id?: SettingId;
    /** Several registered setting ids this block governs - a stack of fields behind one toggle. */
    ids?: readonly SettingId[];
    /**
     * Dynamic-id namespace(s) this block governs, for a family whose ids are synthesized rather than
     * individually registered (an `ElementPropertyRows` prefix, a section-scoped family, ...).
     * Matches the prefix itself or `<prefix>.<anything>`. Accepts one prefix or several.
     */
    idPrefix?: string | readonly string[];
    children: React.ReactNode;
}

/**
 * Wraps a `{cond && <Control/>}` render-gate so a settings deep link can land ON the field instead
 * of bouncing off it.
 *
 * - `when` true: renders `children` exactly as the guard it replaced would. Zero behavior change.
 * - `when` false and no pending `?focus=` names one of this block's ids: renders nothing - today's
 *   behavior, unchanged.
 * - `when` false and a pending `?focus=` DOES name one of its ids: renders `children` anyway, as a
 *   "ghost" - dimmed, inert, with the registry's `condition` sentence for the focused id underneath
 *   it (a generic line if the id has none) and a "View that setting" link when `dependsOn` is set.
 *   The setting is genuinely not part of this configuration; the ghost explains that in place rather
 *   than making the user go read a toast to find out.
 *
 * Do NOT clone or disable `children` - control types vary too much for that to be reliable. The
 * guarantee is interaction-blocking (inert + pointer-events + an overlay), not a disabled prop.
 *
 * PRIVACY - do not wrap a setting gated on an entitlement or CASL permission (an org feature flag,
 * `PRODUCT_PROMOS_ADMIN`, `PRODUCT_RECOMMENDATIONS_ADMIN`, `searchAutocompleteEnabled`, anything
 * procurement, ...). Ghost-rendering one, or giving it a `condition` sentence, discloses a feature
 * the viewer is not entitled to see even though they cannot use it. Those sites keep their plain
 * `{cond && ...}` guard and fall through to the generic "couldn't find it" toast. This component is
 * only for a setting that is absent because of another SETTING's value, never because of who is
 * asking.
 */
const ConditionalSetting: React.FC<IConditionalSettingProps> = ({ when, id, ids, idPrefix, children }) => {
    const focusId = useSettingFocusId();
    const history = useHistory();
    const contentRef = React.useRef<HTMLDivElement | null>(null);

    const ghost = !when && matchesFocus(focusId, id, ids, idPrefix);

    // React 17's JSX typings don't know the `inert` attribute, so it's set imperatively. Also covers
    // keyboard focus/tabbing, which `pointer-events: none` alone does not.
    React.useEffect(() => {
        const node = contentRef.current;
        if (!node || !ghost) return undefined;
        node.setAttribute('inert', '');
        return () => node.removeAttribute('inert');
    }, [ghost]);

    if (when) return <>{children}</>;
    if (!ghost) return null;

    const setting = findSetting(focusId);
    const reason = setting?.condition ?? GENERIC_REASON;
    const dependsOn = setting?.dependsOn;

    return (
        <div>
            <div ref={contentRef} aria-disabled style={{ position: 'relative', opacity: 0.45, filter: 'grayscale(1)', pointerEvents: 'none' }}>
                {children}
                {/* Belt-and-suspenders against a control whose events don't respect an ancestor's
                    pointer-events: none (a portal-rendered picker, say). */}
                <div style={{ position: 'absolute', inset: 0 }} />
            </div>
            <Text block variant='small' styles={{ root: { fontStyle: 'italic', opacity: 0.75, marginTop: 4 } }}>
                {reason}
            </Text>
            {dependsOn && (
                <Link onClick={() => history.push(`/go/setting/${encodeURIComponent(dependsOn)}`)}>
                    View that setting
                </Link>
            )}
        </div>
    );
};

export default ConditionalSetting;
